/**
 * Pure condition resolution for the bounded hypnosis experiment.
 *
 * Protocol values are authored metadata; this module makes the values that
 * reach the player explicit and testable without importing the React shell.
 */

function boundedNumber(value, fallback, minimum, maximum) {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? Math.max(minimum, Math.min(maximum, numeric))
    : fallback;
}

const LOCKED_SCENE_DEFAULTS = Object.freeze({
  condition_lock: true,
  condition_schema: "hypnosis-audio-condition.v1",
  condition_source: "hypnosis_protocol",
  beatMode: "off",
  textureMode: "",
  isochronicMode: "off",
  isochronicLevel: 0,
  entropicDriftEnabled: false,
  bassBrainwaveRippleEnabled: false,
  bassBrainwaveRippleDepth: 0,
  breathWaveEnabled: false,
  breathWaveDepth: 0,
  subliminalBreathDepth: 0,
  beatRateTrajectoryDepthHz: 0,
  beatRateTrajectoryPeriodSeconds: 45,
  somaticSoundEnabled: false,
  heartbeatAudioEnabled: false,
  breathSoundEnabled: false,
  polytemporalEnabled: false,
  polytemporalLevel: 0,
  polytemporalDepth: 0,
  polytemporalDirectAmEnabled: false,
  polytemporalHarmonicEnabled: false,
  polytemporalCrossFrequencyEnabled: false,
  audibleRhythmEnabled: false,
  audibleRhythmMode: "off",
  hapticEnabled: false,
  subliminalLevel: 0,
  backgroundSubliminalLevel: 0,
  subliminalSpectralMode: "in_band",
});

export function resolveProtocolTargetHz(protocol, elapsedSeconds = 0) {
  const trajectory = protocol?.trajectory || {};
  const kind = String(trajectory.kind || "fixed").toLowerCase();
  let hz = Number(trajectory.start_hz ?? protocol?.audio_scene?.binHz ?? 6);
  if (!Number.isFinite(hz)) hz = 6;
  if (kind === "linear") {
    const duration = Math.max(1, Number(trajectory.duration_seconds) || 1);
    const progress = Math.max(0, Math.min(1, Number(elapsedSeconds) / duration));
    const end = Number(trajectory.end_hz ?? hz);
    if (Number.isFinite(end)) hz += (end - hz) * progress;
  } else if (kind === "sine") {
    const center = Number(trajectory.center_hz ?? hz);
    const amplitude = Math.max(0, Math.min(1.5, Number(trajectory.amplitude_hz) || 0));
    const period = Math.max(60, Number(trajectory.period_seconds) || 180);
    hz = (Number.isFinite(center) ? center : hz)
      + amplitude * Math.sin((Math.PI * 2 * Math.max(0, Number(elapsedSeconds))) / period);
  }
  return Math.max(0.5, Math.min(12, Number.isFinite(hz) ? hz : 6));
}

export function applyHypnosisProtocol(track, protocol, elapsedSeconds = 0) {
  if (!track || !protocol?.protocol_id) return track;
  const requested = protocol.audio_scene || {};
  const targetHz = resolveProtocolTargetHz(protocol, elapsedSeconds);
  const lockedDefaults = {
    ...LOCKED_SCENE_DEFAULTS,
    condition_role: protocol.protocol_id === "steady_theta_control" ? "control" : "candidate",
  };
  const boundedScene = {
    ...lockedDefaults,
    ...requested,
    binHz: targetHz,
    targetBrainwaveHz: targetHz,
    // Preserve explicit zeroes so the control condition can actually reach
    // the player. Missing values still receive the historical defaults.
    brainwaveRippleDepth: boundedNumber(requested.brainwaveRippleDepth, 0, 0, 0.003),
    subliminalRippleDepth: boundedNumber(requested.subliminalRippleDepth, 0, 0, 0.12),
    subliminalBreathDepth: boundedNumber(requested.subliminalBreathDepth, 0, 0, 0.5),
    bassBrainwaveRippleDepth: boundedNumber(requested.bassBrainwaveRippleDepth, 0, 0, 0.3),
    beatRateTrajectoryDepthHz: boundedNumber(requested.beatRateTrajectoryDepthHz, 0, 0, 5),
    minimumVoiceGapMs: boundedNumber(requested.minimumVoiceGapMs, 22_000, 12_000, 60_000),
    bpm: boundedNumber(requested.bpm, 36, 18, 90),
  };
  // This function is only used for the assigned experiment protocol. The
  // protocol boundary wins even if a stale or malformed payload says false.
  boundedScene.condition_lock = true;
  return {
    ...track,
    brainwave_target_hz: targetHz,
    audio_scene: { ...(track.audio_scene || {}), ...boundedScene },
    hypnosis_protocol_id: protocol.protocol_id,
  };
}
